import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export default function VocabularyMatching({
  terms = [],
  getTranslationsForTerm,
  getVocabularyProgressForTerm,
  vocabularyLanguage,
  submitVocabularyMatchingResult,
  difficultyStats = [],
  goBack,
  styles,
}) {
  const MATCH_COUNT = 10;

  // =========================================================
  // DIFFICULTY
  // =========================================================

  const [difficultyLevel, setDifficultyLevel] =
    useState("easy");

  const difficultyLevelRef =
    useRef(difficultyLevel);

  useEffect(() => {
    difficultyLevelRef.current =
      difficultyLevel;
  }, [difficultyLevel]);

  // =========================================================
  // GLOBAL DIFFICULTY MAP
  // =========================================================

  const difficultyMap = useMemo(() => {
    return new Map(
      (difficultyStats || []).map((row) => [
        row.term_id,
        row,
      ])
    );
  }, [difficultyStats]);

  // =========================================================
  // LATEST DATA REFS
  //
  // Progress / global stats are allowed to update while
  // the student is doing the round.
  //
  // But these updates must NOT rebuild the current round.
  // They will be used when the NEXT round is generated.
  // =========================================================

  const progressGetterRef = useRef(
    getVocabularyProgressForTerm
  );

  const translationGetterRef = useRef(
    getTranslationsForTerm
  );

  const difficultyMapRef = useRef(
    difficultyMap
  );

  useEffect(() => {
    progressGetterRef.current =
      getVocabularyProgressForTerm;
  }, [getVocabularyProgressForTerm]);

  useEffect(() => {
    translationGetterRef.current =
      getTranslationsForTerm;
  }, [getTranslationsForTerm]);

  useEffect(() => {
    difficultyMapRef.current =
      difficultyMap;
  }, [difficultyMap]);

  // =========================================================
  // FROZEN ROUND DATA
  // =========================================================

  const [roundTerms, setRoundTerms] =
    useState([]);

  const [rightOptions, setRightOptions] =
    useState([]);

  // =========================================================
  // SESSION STATE
  // =========================================================

  const [
    selectedLeftId,
    setSelectedLeftId,
  ] = useState(null);

  const [
    matchedTermIds,
    setMatchedTermIds,
  ] = useState([]);

  const [
    attemptCount,
    setAttemptCount,
  ] = useState(0);

  const [
    incorrectCount,
    setIncorrectCount,
  ] = useState(0);

  const [
    feedback,
    setFeedback,
  ] = useState(null);

  const [
    roundComplete,
    setRoundComplete,
  ] = useState(false);

  const [
    activitySessionId,
    setActivitySessionId,
  ] = useState(() =>
    crypto.randomUUID()
  );

  const [
    wrongAttemptsByTerm,
    setWrongAttemptsByTerm,
  ] = useState({});

  const [
    savingTermIds,
    setSavingTermIds,
  ] = useState([]);

  const [
    saveError,
    setSaveError,
  ] = useState("");

  // =========================================================
  // BUILD ADAPTIVE MATCHING ROUND
  //
  // IMPORTANT:
  // This creates a SNAPSHOT.
  //
  // Once roundTerms / rightOptions are stored in state,
  // they remain frozen for the entire round.
  // =========================================================

  const buildMatchingRound =
    useCallback(
      (requestedDifficulty) => {
        if (!terms.length) {
          return {
            roundTerms: [],
            rightOptions: [],
          };
        }

        // =====================================================
        // HELPERS
        // =====================================================

        const shuffle = (items) => {
          return [...items].sort(
            () => Math.random() - 0.5
          );
        };

        const pickRandom = (
          items,
          count
        ) => {
          return shuffle(items).slice(
            0,
            Math.min(
              count,
              items.length
            )
          );
        };

        // =====================================================
        // GET PERSONAL + GLOBAL ADAPTIVE DATA
        // =====================================================

        const enriched =
          terms.map((term) => {
            const progress =
              progressGetterRef.current?.(
                term.id
              );

            const globalStats =
              difficultyMapRef.current.get(
                term.id
              );

            const masteryLevel =
              progress?.mastery_level ??
              0;

            const reviewCount =
              progress?.review_count ||
              0;

            const correctCount =
              progress?.correct_count ||
              0;

            const personalAccuracy =
              reviewCount > 0
                ? (correctCount /
                    reviewCount) *
                  100
                : null;

            const globalAttempts =
              Number(
                globalStats?.total_attempts ||
                  0
              );

            const globalDifficulty =
              Number(
                globalStats?.difficulty_pct ||
                  0
              );

            // ===============================================
            // CATEGORY
            // ===============================================

            let category = "new";

            if (reviewCount === 0) {
              category = "new";
            } else if (
              masteryLevel >= 3 &&
              personalAccuracy >= 80
            ) {
              category = "strong";
            } else if (
              reviewCount >= 2 &&
              (
                personalAccuracy < 60 ||
                masteryLevel === 0
              )
            ) {
              category = "weak";
            } else {
              category = "learning";
            }

            // ===============================================
            // ADAPTIVE SCORE
            // ===============================================

            const masteryWeakness =
              ((3 - masteryLevel) /
                3) *
              100;

            const personalErrorRate =
              personalAccuracy === null
                ? 0
                : 100 -
                  personalAccuracy;

            const globalWeight =
              Math.min(
                globalAttempts / 20,
                1
              );

            const adjustedGlobalDifficulty =
              globalDifficulty *
              globalWeight;

            const adaptiveScore =
              category === "new"
                ? 50
                : masteryWeakness *
                    0.5 +
                  personalErrorRate *
                    0.3 +
                  adjustedGlobalDifficulty *
                    0.2;

            return {
              term,
              category,
              adaptiveScore,
              masteryLevel,
              reviewCount,
              correctCount,
              personalAccuracy,
              globalDifficulty,
              globalAttempts,
            };
          });

        // =====================================================
        // SPLIT INTO GROUPS
        // =====================================================

        const newWords =
          enriched.filter(
            (item) =>
              item.category === "new"
          );

        const strongWords =
          enriched.filter(
            (item) =>
              item.category ===
              "strong"
          );

        const learningWords =
          enriched.filter(
            (item) =>
              item.category ===
              "learning"
          );

        const weakWords =
          enriched.filter(
            (item) =>
              item.category === "weak"
          );

        let selected = [];

        // =====================================================
        // EASY
        //
        // 7 easier known words
        // 3 new words
        // =====================================================

        if (
          requestedDifficulty ===
          "easy"
        ) {
          const easierKnown = [
            ...strongWords,
            ...learningWords,
          ].sort(
            (a, b) =>
              a.adaptiveScore -
              b.adaptiveScore
          );

          const easyCandidatePool =
            easierKnown.slice(
              0,
              Math.max(
                MATCH_COUNT,
                Math.ceil(
                  easierKnown.length *
                    0.6
                )
              )
            );

          selected = [
            ...pickRandom(
              easyCandidatePool,
              7
            ),

            ...pickRandom(
              newWords,
              3
            ),
          ];
        }

        // =====================================================
        // NORMAL
        //
        // 3 Strong
        // 4 New / Learning
        // 3 Weak
        // =====================================================

        if (
          requestedDifficulty ===
          "normal"
        ) {
          selected = [
            ...pickRandom(
              strongWords,
              3
            ),

            ...pickRandom(
              [
                ...newWords,
                ...learningWords,
              ],
              4
            ),

            ...pickRandom(
              weakWords,
              3
            ),
          ];
        }

        // =====================================================
        // HARD
        //
        // 7 Weak
        // 3 New / Learning
        // =====================================================

        if (
          requestedDifficulty ===
          "hard"
        ) {
          const hardestWeak = [
            ...weakWords,
          ].sort(
            (a, b) =>
              b.adaptiveScore -
              a.adaptiveScore
          );

          const hardCandidatePool =
            hardestWeak.slice(
              0,
              Math.max(
                MATCH_COUNT,
                Math.ceil(
                  hardestWeak.length *
                    0.7
                )
              )
            );

          selected = [
            ...pickRandom(
              hardCandidatePool,
              7
            ),

            ...pickRandom(
              [
                ...newWords,
                ...learningWords,
              ],
              3
            ),
          ];
        }

        // =====================================================
        // REMOVE DUPLICATES
        // =====================================================

        const selectedMap =
          new Map();

        selected.forEach((item) => {
          selectedMap.set(
            item.term.id,
            item
          );
        });

        selected = [
          ...selectedMap.values(),
        ];

        // =====================================================
        // FALLBACK
        //
        // Fill remaining spots from unused topic terms.
        // =====================================================

        if (
          selected.length <
          MATCH_COUNT
        ) {
          const selectedIds =
            new Set(
              selected.map(
                (item) =>
                  item.term.id
              )
            );

          const remaining =
            enriched.filter(
              (item) =>
                !selectedIds.has(
                  item.term.id
                )
            );

          selected = [
            ...selected,

            ...pickRandom(
              remaining,
              MATCH_COUNT -
                selected.length
            ),
          ];
        }

        // =====================================================
        // FINAL 10 TERMS
        // =====================================================

        const finalSelected =
          shuffle(selected).slice(
            0,
            Math.min(
              MATCH_COUNT,
              selected.length
            )
          );

        const newRoundTerms =
          finalSelected.map(
            (item) => item.term
          );

        // =====================================================
        // BUILD + FREEZE RIGHT-SIDE OPTIONS
        // =====================================================

        const newRightOptions =
          shuffle(
            newRoundTerms.map(
              (term) => {
                const translations =
                  translationGetterRef.current?.(
                    term.id
                  ) || {};

                return {
                  termId: term.id,

                  translation:
                    translations?.[
                      vocabularyLanguage
                    ] || "",
                };
              }
            )
          );

        return {
          roundTerms:
            newRoundTerms,

          rightOptions:
            newRightOptions,
        };
      },
      [
        terms,
        vocabularyLanguage,
      ]
    );

  // =========================================================
  // RESET ROUND STATE
  // =========================================================

  const resetRoundState =
    useCallback(() => {
      setSelectedLeftId(null);

      setMatchedTermIds([]);

      setAttemptCount(0);

      setIncorrectCount(0);

      setFeedback(null);

      setRoundComplete(false);

      setWrongAttemptsByTerm({});

      setSavingTermIds([]);

      setSaveError("");

      setActivitySessionId(
        crypto.randomUUID()
      );
    }, []);

  // =========================================================
  // START NEW FROZEN ROUND
  // =========================================================

  const startMatchingRound =
    useCallback(
      (requestedDifficulty) => {
        const newRound =
          buildMatchingRound(
            requestedDifficulty
          );

        setRoundTerms(
          newRound.roundTerms
        );

        setRightOptions(
          newRound.rightOptions
        );

        resetRoundState();
      },
      [
        buildMatchingRound,
        resetRoundState,
      ]
    );

  // =========================================================
  // INITIAL ROUND / TOPIC OR LANGUAGE CHANGE
  //
  // Progress updates do NOT regenerate the round.
  // =========================================================

  useEffect(() => {
    if (terms.length > 0) {
      startMatchingRound(
        difficultyLevelRef.current
      );
    } else {
      setRoundTerms([]);
      setRightOptions([]);
    }
  }, [
    terms,
    vocabularyLanguage,
    startMatchingRound,
  ]);

  // =========================================================
  // CLEAR FEEDBACK
  // =========================================================

  const clearFeedbackSoon = () => {
    setTimeout(() => {
      setFeedback(null);
    }, 700);
  };

  // =========================================================
  // HANDLE LEFT SELECT
  // =========================================================

  const handleLeftSelect = (
    termId
  ) => {
    if (
      matchedTermIds.includes(
        termId
      ) ||
      savingTermIds.length > 0
    ) {
      return;
    }

    setSelectedLeftId(termId);

    setFeedback(null);

    setSaveError("");
  };

  // =========================================================
  // HANDLE RIGHT SELECT
  // =========================================================

  const handleRightSelect =
    async (rightTermId) => {
      if (!selectedLeftId) {
        setFeedback({
          type: "incorrect",
          message:
            "Select an English term first.",
        });

        clearFeedbackSoon();

        return;
      }

      if (
        matchedTermIds.includes(
          rightTermId
        ) ||
        savingTermIds.length > 0
      ) {
        return;
      }

      setAttemptCount(
        (prev) => prev + 1
      );

      setSaveError("");

      // =====================================================
      // INCORRECT
      // =====================================================

      if (
        selectedLeftId !==
        rightTermId
      ) {
        const wrongTermId =
          selectedLeftId;

        setIncorrectCount(
          (prev) => prev + 1
        );

        setWrongAttemptsByTerm(
          (prev) => ({
            ...prev,

            [wrongTermId]:
              (prev[wrongTermId] ||
                0) + 1,
          })
        );

        setFeedback({
          type: "incorrect",
          message:
            "Not a match. Try again.",
        });

        // Keep left word selected.
        clearFeedbackSoon();

        return;
      }

      // =====================================================
      // CORRECT
      // =====================================================

      const termId =
        selectedLeftId;

      const wrongAttempts =
        wrongAttemptsByTerm[
          termId
        ] || 0;

      setFeedback({
        type: "correct",

        message:
          wrongAttempts === 0
            ? "Correct!"
            : "Correct after retry!",
      });

      setSavingTermIds(
        (prev) => [
          ...prev,
          termId,
        ]
      );

      const result =
        await submitVocabularyMatchingResult(
          {
            termId,

            sessionId:
              activitySessionId,

            wrongAttempts,
          }
        );

      setSavingTermIds(
        (prev) =>
          prev.filter(
            (id) =>
              id !== termId
          )
      );

      // =====================================================
      // DATABASE ERROR
      // =====================================================

      if (!result?.success) {
        setSaveError(
          result?.error ||
            "Failed to save matching result."
        );

        setFeedback(null);

        return;
      }

      // =====================================================
      // MARK MATCHED
      // =====================================================

      const nextMatched = [
        ...matchedTermIds,
        termId,
      ];

      setMatchedTermIds(
        nextMatched
      );

      setSelectedLeftId(null);

      // =====================================================
      // COMPLETE ROUND
      // =====================================================

      if (
        nextMatched.length ===
        roundTerms.length
      ) {
        setTimeout(() => {
          setFeedback(null);

          setRoundComplete(true);
        }, 600);

        return;
      }

      clearFeedbackSoon();
    };

  // =========================================================
  // CHANGE DIFFICULTY
  // =========================================================

  const changeDifficulty = (
    level
  ) => {
    if (
      savingTermIds.length > 0
    ) {
      return;
    }

    if (
      level ===
      difficultyLevel
    ) {
      return;
    }

    setDifficultyLevel(level);

    difficultyLevelRef.current =
      level;

    startMatchingRound(level);
  };

  // =========================================================
  // PLAY SAME LEVEL AGAIN
  // =========================================================

  const startNewRound = () => {
    startMatchingRound(
      difficultyLevel
    );
  };

  // =========================================================
  // DIFFICULTY LABEL
  // =========================================================

  const getDifficultyDisplayName =
    (level) => {
      if (level === "easy") {
        return "Easy";
      }

      if (level === "hard") {
        return "Hard";
      }

      return "Normal";
    };

  const getDifficultyLabel = () =>
    getDifficultyDisplayName(
      difficultyLevel
    );

  // =========================================================
  // AUTO RECOMMENDATION
  // =========================================================

  const getRecommendedDifficulty =
    () => {
      if (!roundComplete) {
        return difficultyLevel;
      }

      const firstTryRate =
        roundTerms.length > 0
          ? Math.round(
              (
                roundTerms.filter(
                  (term) =>
                    (
                      wrongAttemptsByTerm[
                        term.id
                      ] || 0
                    ) === 0
                ).length /
                roundTerms.length
              ) *
                100
            )
          : 0;

      // EASY
      if (
        difficultyLevel ===
        "easy"
      ) {
        return firstTryRate >= 90
          ? "normal"
          : "easy";
      }

      // NORMAL
      if (
        difficultyLevel ===
        "normal"
      ) {
        if (
          firstTryRate >= 85
        ) {
          return "hard";
        }

        if (
          firstTryRate < 65
        ) {
          return "easy";
        }

        return "normal";
      }

      // HARD
      if (
        difficultyLevel ===
        "hard"
      ) {
        if (
          firstTryRate < 60
        ) {
          return "normal";
        }

        return "hard";
      }

      return difficultyLevel;
    };

  // =========================================================
  // CONTINUE RECOMMENDED LEVEL
  // =========================================================

  const continueWithRecommended =
    (recommendedDifficulty) => {
      setDifficultyLevel(
        recommendedDifficulty
      );

      difficultyLevelRef.current =
        recommendedDifficulty;

      startMatchingRound(
        recommendedDifficulty
      );
    };

  // =========================================================
  // EMPTY STATE
  // =========================================================

  if (!terms.length) {
    return (
      <div style={styles.card}>
        <button
          style={
            styles.secondaryButton
          }
          onClick={goBack}
        >
          Back
        </button>

        <div
          style={styles.emptyBox}
        >
          No vocabulary available
          for matching.
        </div>
      </div>
    );
  }

  // =========================================================
  // INITIALIZING
  // =========================================================

  if (
    roundTerms.length === 0
  ) {
    return (
      <div style={styles.card}>
        <div
          style={styles.emptyBox}
        >
          Preparing matching
          round...
        </div>
      </div>
    );
  }

  // =========================================================
  // ROUND COMPLETE
  // =========================================================

  if (roundComplete) {
    const accuracy =
      attemptCount > 0
        ? Math.round(
            (roundTerms.length /
              attemptCount) *
              100
          )
        : 100;

    const firstTryCorrectCount =
      roundTerms.filter(
        (term) =>
          (
            wrongAttemptsByTerm[
              term.id
            ] || 0
          ) === 0
      ).length;

    const retryWordCount =
      roundTerms.length -
      firstTryCorrectCount;

    const firstTryRate =
      roundTerms.length > 0
        ? Math.round(
            (firstTryCorrectCount /
              roundTerms.length) *
              100
          )
        : 0;

    const recommendedDifficulty =
      getRecommendedDifficulty();

    return (
      <div style={styles.card}>
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
          }}
        >
          {/* ===============================================
              COMPLETE HEADER
          =============================================== */}

          <div
            style={{
              fontSize: "42px",
              marginBottom: "12px",
            }}
          >
            🎉
          </div>

          <h2
            style={
              styles.sectionTitle
            }
          >
            Matching Complete
          </h2>

          <div
            style={{
              marginTop: "10px",
              color: "#6b7280",
            }}
          >
            You completed this{" "}
            {getDifficultyLabel()}{" "}
            round of{" "}
            {roundTerms.length}{" "}
            words.
          </div>

          {/* ===============================================
              ROUND STATS
          =============================================== */}

          <div
            style={{
              marginTop: "24px",

              display: "grid",

              gridTemplateColumns:
                "repeat(auto-fit, minmax(120px, 1fr))",

              gap: "12px",
            }}
          >
            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Words
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {
                  roundTerms.length
                }
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Attempts
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {attemptCount}
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Incorrect
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {
                  incorrectCount
                }
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Accuracy
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {accuracy}%
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                First Try
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {
                  firstTryCorrectCount
                }
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Needed Retry
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {retryWordCount}
              </div>
            </div>
          </div>

          {/* ===============================================
              AUTO RECOMMENDATION
          =============================================== */}

          <div
            style={{
              marginTop: "24px",

              padding: "18px",

              border:
                "1px solid #dbeafe",

              borderRadius: "14px",

              backgroundColor:
                "#eff6ff",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "700",
                color: "#6b7280",
              }}
            >
              Recommended Next
              Level
            </div>

            <div
              style={{
                marginTop: "8px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              {getDifficultyDisplayName(
                recommendedDifficulty
              )}
            </div>

            <div
              style={{
                marginTop: "8px",
                color: "#6b7280",
              }}
            >
              Your first-try
              accuracy was{" "}
              <strong>
                {firstTryRate}%
              </strong>
              .
            </div>

            <div
              style={{
                marginTop: "6px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              {recommendedDifficulty ===
              difficultyLevel
                ? `Keep practicing at ${getDifficultyDisplayName(
                    recommendedDifficulty
                  )} level.`
                : `Based on this round, we recommend moving to ${getDifficultyDisplayName(
                    recommendedDifficulty
                  )}.`}
            </div>
          </div>

          {/* ===============================================
              ACTIONS
          =============================================== */}

          <div
            style={{
              marginTop: "28px",

              display: "flex",

              gap: "12px",

              justifyContent:
                "center",

              flexWrap: "wrap",
            }}
          >
            <button
              style={
                styles.smallPrimaryButton
              }
              onClick={() =>
                continueWithRecommended(
                  recommendedDifficulty
                )
              }
            >
              Continue with{" "}
              {getDifficultyDisplayName(
                recommendedDifficulty
              )}
            </button>

            <button
              style={
                styles.secondaryButton
              }
              onClick={
                startNewRound
              }
            >
              Play{" "}
              {getDifficultyLabel()}{" "}
              Again
            </button>

            <button
              style={
                styles.secondaryButton
              }
              onClick={goBack}
            >
              Finish
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // MATCHING UI
  // =========================================================

  return (
    <div style={styles.card}>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div style={styles.topBar}>
        <div>
          <h2
            style={
              styles.sectionTitle
            }
          >
            Vocabulary Matching
          </h2>

          <div
            style={
              styles.quizPath
            }
          >
            Match each English
            term with its
            translation.
          </div>
        </div>

        <button
          style={
            styles.secondaryButton
          }
          onClick={goBack}
          disabled={
            savingTermIds.length >
            0
          }
        >
          Back
        </button>
      </div>

      {/* =====================================================
          DIFFICULTY SELECTOR
      ===================================================== */}

      <div
        style={{
          display: "flex",

          alignItems: "center",

          gap: "10px",

          marginTop: "18px",

          flexWrap: "wrap",
        }}
      >
        <strong>
          Difficulty:
        </strong>

        {[
          "easy",
          "normal",
          "hard",
        ].map((level) => (
          <button
            key={level}
            onClick={() =>
              changeDifficulty(
                level
              )
            }
            disabled={
              savingTermIds.length >
              0
            }
            style={{
              padding:
                "8px 14px",

              borderRadius:
                "8px",

              border:
                difficultyLevel ===
                level
                  ? "2px solid #2563eb"
                  : "1px solid #d1d5db",

              backgroundColor:
                difficultyLevel ===
                level
                  ? "#eff6ff"
                  : "#ffffff",

              fontWeight:
                difficultyLevel ===
                level
                  ? "700"
                  : "500",

              cursor:
                savingTermIds.length >
                0
                  ? "default"
                  : "pointer",
            }}
          >
            {getDifficultyDisplayName(
              level
            )}
          </button>
        ))}

        <div
          style={{
            marginLeft: "6px",

            color: "#6b7280",

            fontSize: "13px",
          }}
        >
          Current:{" "}
          <strong>
            {getDifficultyLabel()}
          </strong>
        </div>
      </div>

      {/* =====================================================
          STATUS
      ===================================================== */}

      <div
        style={{
          marginTop: "20px",

          display: "flex",

          gap: "18px",

          color: "#6b7280",

          flexWrap: "wrap",

          alignItems: "center",
        }}
      >
        <div>
          Matched:{" "}
          <strong>
            {
              matchedTermIds.length
            }{" "}
            /{" "}
            {roundTerms.length}
          </strong>
        </div>

        <div>
          Attempts:{" "}
          <strong>
            {attemptCount}
          </strong>
        </div>

        <div>
          Incorrect:{" "}
          <strong>
            {incorrectCount}
          </strong>
        </div>

        {savingTermIds.length >
          0 && (
          <div>
            <strong>
              Saving progress...
            </strong>
          </div>
        )}
      </div>

      {/* =====================================================
          FEEDBACK
      ===================================================== */}

      {feedback && (
        <div
          style={{
            marginTop: "16px",

            padding:
              "10px 14px",

            borderRadius:
              "10px",

            backgroundColor:
              feedback.type ===
              "correct"
                ? "#dcfce7"
                : "#fee2e2",

            color:
              feedback.type ===
              "correct"
                ? "#166534"
                : "#991b1b",

            fontWeight: "700",
          }}
        >
          {feedback.message}
        </div>
      )}

      {/* =====================================================
          DATABASE ERROR
      ===================================================== */}

      {saveError && (
        <div
          style={{
            marginTop: "12px",

            padding:
              "10px 14px",

            borderRadius:
              "10px",

            backgroundColor:
              "#fee2e2",

            color: "#991b1b",

            fontWeight: "600",
          }}
        >
          Unable to save
          progress:{" "}
          {saveError}
        </div>
      )}

      {/* =====================================================
          MATCHING GRID
      ===================================================== */}

      <div
        style={{
          marginTop: "28px",
        }}
      >
        {/* ===================================================
            HEADERS
        =================================================== */}

        <div
          style={{
            display: "grid",

            gridTemplateColumns:
              "repeat(2, minmax(0, 1fr))",

            columnGap: "24px",

            marginBottom: "12px",
          }}
        >
          <h3
            style={{
              margin: 0,
            }}
          >
            English
          </h3>

          <h3
            style={{
              margin: 0,
            }}
          >
            {vocabularyLanguage ===
            "zh"
              ? "中文"
              : vocabularyLanguage ===
                "es"
              ? "Español"
              : "Русский"}
          </h3>
        </div>

        {/* ===================================================
            ROWS
        =================================================== */}

        <div
          style={{
            display: "grid",

            gridTemplateColumns:
              "repeat(2, minmax(0, 1fr))",

            columnGap: "24px",

            rowGap: "10px",
          }}
        >
          {roundTerms.map(
            (term, index) => {
              const option =
                rightOptions[
                  index
                ];

              const leftMatched =
                matchedTermIds.includes(
                  term.id
                );

              const leftSelected =
                selectedLeftId ===
                term.id;

              const leftSaving =
                savingTermIds.includes(
                  term.id
                );

              const rightMatched =
                option
                  ? matchedTermIds.includes(
                      option.termId
                    )
                  : false;

              const rightSaving =
                option
                  ? savingTermIds.includes(
                      option.termId
                    )
                  : false;

              return (
                <React.Fragment
                  key={term.id}
                >
                  {/* =========================================
                      LEFT — ENGLISH
                  ========================================= */}

                  <button
                    disabled={
                      leftMatched ||
                      savingTermIds.length >
                        0
                    }
                    onClick={() =>
                      handleLeftSelect(
                        term.id
                      )
                    }
                    style={{
                      width: "100%",

                      minHeight:
                        "68px",

                      padding: "16px",

                      borderRadius:
                        "12px",

                      border:
                        leftSelected
                          ? "2px solid #2563eb"
                          : "1px solid #d1d5db",

                      backgroundColor:
                        leftMatched
                          ? "#dcfce7"
                          : leftSelected
                          ? "#eff6ff"
                          : "#ffffff",

                      textAlign:
                        "left",

                      fontSize:
                        "15px",

                      fontWeight:
                        "600",

                      cursor:
                        leftMatched ||
                        savingTermIds.length >
                          0
                          ? "default"
                          : "pointer",

                      opacity:
                        leftMatched ||
                        leftSaving
                          ? 0.6
                          : 1,
                    }}
                  >
                    {leftMatched &&
                      "✓ "}

                    {leftSaving &&
                      "Saving... "}

                    {
                      term.english_term
                    }
                  </button>

                  {/* =========================================
                      RIGHT — TRANSLATION
                  ========================================= */}

                  {option ? (
                    <button
                      disabled={
                        rightMatched ||
                        savingTermIds.length >
                          0
                      }
                      onClick={() =>
                        handleRightSelect(
                          option.termId
                        )
                      }
                      style={{
                        width:
                          "100%",

                        minHeight:
                          "68px",

                        padding:
                          "16px",

                        borderRadius:
                          "12px",

                        border:
                          "1px solid #d1d5db",

                        backgroundColor:
                          rightMatched
                            ? "#dcfce7"
                            : "#ffffff",

                        textAlign:
                          "left",

                        fontSize:
                          "15px",

                        cursor:
                          rightMatched ||
                          savingTermIds.length >
                            0
                            ? "default"
                            : "pointer",

                        opacity:
                          rightMatched ||
                          rightSaving
                            ? 0.6
                            : 1,
                      }}
                    >
                      {rightMatched &&
                        "✓ "}

                      {rightSaving &&
                        "Saving... "}

                      {
                        option.translation
                      }
                    </button>
                  ) : (
                    <div />
                  )}
                </React.Fragment>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}